import React, { useMemo, useState } from 'react';
import { useAuth } from 'hooks/useAuth';
import axiosInstance from 'api/axios.config';
import { BRAND_CONFIG } from 'constants/branding';
import { identify } from './verifiedClients';

/**
 * The one screen where somebody is told what they are granting.
 *
 * An assistant — Claude, ChatGPT — sends the person here to approve access to their
 * account. Before this existed the MCP server showed its own form asking for a password,
 * which is the shape of a phishing flow and the thing a connector review rejects. Now the
 * password only ever goes to the portal, and what leaves here is a one-time code.
 *
 * Nothing is granted until the button is pressed.
 */

/**
 * Scopes in words, not names.
 *
 * `calls:originate` tells a developer what it does and tells a business owner nothing.
 * This screen is the ONLY place a person is told what they are agreeing to, so every line
 * says what the assistant will be able to do, in the second person, and the ones that cost
 * money or reach a customer say so.
 *
 * `group` is what the reader actually scans by. Seven flat rows read as a wall and get
 * skimmed; four labelled groups read as "it can message, call, run campaigns and see
 * leads", which is the sentence somebody needs in order to refuse.
 */
export const SCOPE_TEXT: Record<
  string,
  { label: string; detail: string; group: string; heavy?: boolean }
> = {
  'whatsapp:send':    { group: 'WhatsApp', label: 'Send WhatsApp messages', heavy: true,
                        detail: 'Messages your customers receive from your number.' },
  'messages:read':    { group: 'WhatsApp', label: 'See message status',
                        detail: 'Whether a WhatsApp message was delivered or read.' },

  'calls:originate':  { group: 'Calls', label: 'Place calls to your customers', heavy: true,
                        detail: 'Real phone calls, which cost money and ring a real person.' },
  'calls:manage':     { group: 'Calls', label: 'End a call in progress', heavy: true,
                        detail: 'Hang up a call while somebody is on it.' },
  'calls:read':       { group: 'Calls', label: 'See call details',
                        detail: 'The status and outcome of calls already placed, your call log, '
                                + 'stored call transcripts and summaries, and volume/duration '
                                + 'totals. Read only.' },

  'campaigns:read':   { group: 'Campaigns', label: 'See your campaigns',
                        detail: 'Lists, delivery figures and insights, and your WhatsApp '
                                + 'message templates. Read only.' },
  // Heavy: one launch messages everybody on a list, and Meta charges per conversation.
  'campaigns:send':   { group: 'Campaigns', label: 'Create and send WhatsApp campaigns', heavy: true,
                        detail: 'Create a campaign as a draft for a list of people and, once you '
                                + 'confirm, send it to all of them or pause it. Every message '
                                + 'reaches a real customer and is charged.' },

  'leads:read':       { group: 'Leads', label: 'See your captured leads',
                        detail: 'Names, numbers and details captured from calls and chats, '
                                + 'and the figures over them. Read only.' },

  // Not heavy: it corrects your own records and reaches nobody. Never deletes, never
  // touches the caller's number, and never writes to your CRM.
  'leads:write':      { group: 'Leads', label: 'Correct and add leads',
                        detail: 'Change a captured lead\'s details or status, or add a lead by '
                                + 'hand. It cannot delete a lead or change the caller\'s number, '
                                + 'and a lead already in your CRM is not updated there.' },

  'contacts:read':    { group: 'Contacts', label: 'See your contacts',
                        detail: 'Names, numbers, companies and message history for everyone in '
                                + 'your address book — not only the ones a call or chat captured. '
                                + 'Read only.' },
  // Not heavy: it changes your own address book and reaches nobody. Never deletes.
  'contacts:write':   { group: 'Contacts', label: 'Add and edit your contacts',
                        detail: 'Create a contact, and change a name, company, notes or labels. '
                                + 'It cannot delete or merge contacts, or change their numbers.' },

  'metrics:read':     { group: 'Reports', label: 'See your overall numbers',
                        detail: 'Call volume, dashboard totals and report summaries — the '
                                + 'aggregate figures, not individual customer records. Read only.' },

  'agents:read':      { group: 'AI agents', label: 'See your AI agents',
                        detail: 'What each agent is called, how it is configured and whether it '
                                + 'reached the voice runtime. Not the prompts or tools, and it '
                                + 'cannot change anything. Read only.' },

  // Heavy: the agent says this to every caller from the next call on.
  'agents:write':     { group: 'AI agents', label: 'Change what your AI agent says', heavy: true,
                        detail: 'Change an agent\'s greeting, instructions or opening line. Every '
                                + 'caller hears the change from the next call. It cannot change '
                                + 'the voice, language, tools or numbers, or create or delete an agent.' },

  // The one write on this screen NOT marked heavy, and the only :manage scope that
  // isn't. `heavy` warns that something reaches a customer or costs money; this does
  // the opposite — it is how an assistant acts on "stop contacting me". Warning about
  // it the way we warn about dialling a list would discourage granting the one
  // permission here that protects the person on the other end.
  'dnd:manage':       { group: 'Do not contact', label: 'Add people to your do-not-contact list',
                        detail: 'Record that somebody must not be contacted again, and check '
                                + 'whether they already are. It cannot remove anyone from the '
                                + 'list — only add.' },

  // Heavy: it makes a link that takes real money once someone pays it. The wallet is
  // credited only when the payment gateway confirms, never by the link itself.
  'billing:pay':      { group: 'Billing', label: 'Create links to top up your wallet', heavy: true,
                        detail: 'Create a payment link for an amount you choose, to add prepaid '
                                + 'credit. Nothing is charged until someone opens the link and '
                                + 'pays it. Only account owners can create one.' },
  'billing:read':     { group: 'Billing', label: 'See your billing',
                        detail: 'Your wallet balance, this month\'s usage and charges, and your '
                                + 'invoices with what is paid and due. Read only — it cannot add '
                                + 'credit, pay or change anything.' },
};

/** The order groups appear in: what reaches a customer first, what only looks, last. */
export const GROUP_ORDER =
  ['WhatsApp', 'Calls', 'Campaigns', 'AI agents', 'Billing', 'Do not contact', 'Leads', 'Contacts',
   'Reports'];

const Authorize: React.FC = () => {
  const { isAuthenticated, loading, user, currentOrganization, logout } = useAuth();
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const clientId = params.get('client_id') || '';
  const redirectUri = params.get('redirect_uri') || '';
  const state = params.get('state') || '';
  const scopes = (params.get('scope') || '').split(/[ ,+]+/).filter(Boolean);

  /**
   * What the application CALLS itself. A claim, and shown as one.
   *
   * It is self-asserted at registration, which is unauthenticated by design — that is
   * what lets an assistant register itself — so a convincing name proves nothing. The
   * MCP server already strips invisible characters and refuses a name borrowing one of
   * our brands; this re-trims and re-caps because a query string is not a trusted
   * channel either, and it must never be the only thing identifying the application.
   */
  const claimedName = (params.get('client_name') || '').replace(/\s+/g, ' ').trim().slice(0, 64);

  /**
   * Where the authorisation code will actually be delivered.
   *
   * THIS is the identity that cannot be faked. The redirect was matched against what the
   * client registered, and the code physically goes to this host — so "access will be
   * sent to claude.ai" is evidence in a way that any name is not. It is shown with more
   * weight than the name for exactly that reason.
   */
  const clientHost = (params.get('client_host') || '').trim().slice(0, 100);

  /**
   * What we are willing to assert about this application, keyed on the HOST.
   *
   * <p>Safe to render only because the MCP server now signs `client_host` and
   * core-service verifies it — a badge keyed on a forgeable parameter would have put our
   * own assurance behind the thing it was meant to warn about. See verifiedClients.ts.
   */
  const identity = identify(clientHost, claimedName);

  /** This page, query string and all — what any sign-in detour has to come back to. */
  const here = window.location.pathname + window.location.search;

  if (loading) return <div style={{ padding: 48, fontFamily: 'system-ui, sans-serif' }}>Loading…</div>;

  // Not signed in: send them to log in and come straight back here, query string intact.
  // Without the return trip the authorisation is abandoned half-done.
  if (!isAuthenticated) {
    window.location.replace(`/login?next=${encodeURIComponent(here)}`);
    return <div style={{ padding: 48, fontFamily: 'system-ui, sans-serif' }}>Redirecting to sign in…</div>;
  }

  const bad = !clientId || !redirectUri || scopes.length === 0;

  /**
   * Sign in as somebody else, and come back to this same request.
   *
   * <p>People reach this screen from an assistant, on whatever browser profile happens to
   * be signed in — often an account that is not the one they mean to connect. Without
   * this they have to find the portal, sign out, sign in, and start the whole
   * authorisation again from the assistant. The `next` round-trip is already built for
   * the not-signed-in case; this reuses it.
   */
  const useAnotherAccount = () => {
    logout();
    window.location.replace(`/login?next=${encodeURIComponent(here)}`);
  };

  /** Approve: ask cpass for a one-time code, then hand it back to the client. */
  const approve = async () => {
    setWorking(true);
    setError(null);
    try {
      // The identity this screen actually showed, passed back for verification.
      //
      // This page cannot check its own query string — it is browser JavaScript and holds
      // no secret — so it forwards what it was given and cpass decides. The MCP server
      // signed these four values together; if somebody linked the person here with a
      // name or host they typed themselves, the signature will not match and no code is
      // minted. That is why the name and host are sent as well as the signature: cpass
      // has to re-derive the signature over exactly what was on screen, not over what it
      // might otherwise have assumed.
      // RAW query values, deliberately — not `claimedName` and `clientHost`.
      //
      // Those two are re-processed for RENDERING: collapsed whitespace, trimmed, length
      // capped, because this screen must not be laid out by whatever a client called
      // itself. The MCP server signed what it put IN the URL. Sending the display
      // versions would mean cpass re-deriving the signature over a string that had been
      // through a second, different cleanup — and any divergence at all, a trimmed space
      // or one character of cap, fails every sign-in closed. Sanitise for the eye,
      // transmit verbatim for the signature.
      const res = await axiosInstance.post('/v1/oauth/delegate', {
        client_id: clientId,
        scopes,
        client_name: params.get('client_name') || undefined,
        client_host: params.get('client_host') || undefined,
        client_sig: params.get('client_sig') || undefined,
      });
      const code = res.data?.data?.code;
      if (!code) throw new Error('no code returned');

      const back = new URL(redirectUri);
      back.searchParams.set('code', code);
      if (state) back.searchParams.set('state', state);
      window.location.replace(back.toString());
    } catch (e: any) {
      setWorking(false);
      setError(e?.response?.data?.message || e?.message || 'Could not authorise.');
    }
  };

  /** Deny: tell the client, in the words the OAuth spec gives for it. */
  const deny = () => {
    if (!redirectUri) { window.location.replace('/'); return; }
    const back = new URL(redirectUri);
    back.searchParams.set('error', 'access_denied');
    if (state) back.searchParams.set('state', state);
    window.location.replace(back.toString());
  };

  const page: React.CSSProperties = {
    minHeight: '100vh', background: '#f7f8fa',
    fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif', lineHeight: 1.55,
  };
  const card: React.CSSProperties = {
    maxWidth: 560, margin: '0 auto', padding: '40px 20px 56px',
  };

  if (bad) {
    return (
      <div style={page}>
        <div style={card}>
          <h1 style={{ fontSize: 20, margin: '0 0 8px' }}>Something is missing from this request</h1>
          <p style={{ color: '#4b5563' }}>
            The application did not say who it is, where to send you back, or what it wants
            access to. Nothing has been granted. Close this and try again from the application.
          </p>
        </div>
      </div>
    );
  }

  const groups = GROUP_ORDER
    .map((g) => ({ name: g, items: scopes.filter((s) => SCOPE_TEXT[s]?.group === g) }))
    .filter((g) => g.items.length > 0);
  // Anything we have no wording for still has to appear. A permission nobody can read is
  // one nobody can refuse, so it is shown as unrecognised rather than quietly dropped.
  const unknown = scopes.filter((s) => !SCOPE_TEXT[s]);
  const anyHeavy = scopes.some((s) => SCOPE_TEXT[s]?.heavy);
  const who = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.username;

  return (
    <div style={page}>
      <div style={card}>
        <p style={{ fontSize: 12, letterSpacing: '.08em', textTransform: 'uppercase',
                    color: '#6b7280', margin: '0 0 10px', fontWeight: 600 }}>
          {BRAND_CONFIG.brandName}
        </p>
        <h1 style={{ fontSize: 22, margin: '0 0 6px', letterSpacing: '-0.01em' }}>
          {claimedName
            ? <>Connect <span style={{ whiteSpace: 'nowrap' }}>“{claimedName}”</span> to your account</>
            : 'Connect an application to your account'}
        </h1>
        <p style={{ color: '#4b5563', margin: '0 0 16px' }}>
          It is asking for the permissions below. Nothing happens until you allow it.
        </p>

        {/* The part that is evidence rather than a claim. */}
        {clientHost && (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                        padding: '12px 16px', marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Access will be sent to</div>
            <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
              {clientHost}
              {identity.verified && (
                <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: '#065f46',
                               background: '#d1fae5', border: '1px solid #6ee7b7',
                               borderRadius: 4, padding: '2px 6px', whiteSpace: 'nowrap' }}>
                  ✓ VERIFIED {identity.verifiedName?.toUpperCase()}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              {identity.verified
                ? <>We recognise this address as {identity.verifiedName}. That is what the tick
                    means and all it means — it does not vouch for what you are about to grant,
                    so the permissions below are still worth reading.</>
                : claimedName
                  ? <>The name above is what this application calls itself. This address is where
                      your access actually goes — if you do not recognise it, press Deny.</>
                  : <>This is where your access actually goes. If you do not recognise it, press Deny.</>}
            </div>
          </div>
        )}

        {/*
          The one case where we know something is actively wrong, rather than merely
          unrecognised: it calls itself a name we know and arrives from a host we do not.
          Louder than "unverified" on purpose — that is the difference between "we cannot
          vouch for this" and "this is claiming to be something it is not".
        */}
        {identity.impersonating && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
                        padding: '12px 16px', marginBottom: 12, color: '#991b1b' }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>This is not the application it claims to be</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              It calls itself “{claimedName}”, but your access would be sent to{' '}
              <strong style={{ overflowWrap: 'anywhere' }}>{clientHost}</strong>, which is not an
              address we recognise for it. Press Deny unless you are certain you know what this is.
            </div>
          </div>
        )}

        {/* Who this would be granted as — the thing people get wrong, because they arrive
            from an assistant on whatever profile the browser was already signed into. */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                      padding: '14px 16px', marginBottom: 18 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 240px', minWidth: 0 }}>
              <div style={{ fontSize: 12, color: '#6b7280' }}>Access would be granted as</div>
              <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                {who}
                {user?.email ? (
                  <span style={{ fontWeight: 400, color: '#4b5563' }}> · {user.email}</span>
                ) : null}
              </div>
              {currentOrganization?.name && (
                <div style={{ fontSize: 13, color: '#4b5563' }}>{currentOrganization.name}</div>
              )}
            </div>
            <button
              type="button"
              onClick={useAnotherAccount}
              disabled={working}
              style={{ background: 'none', border: 0, padding: 0, color: '#1d4ed8',
                       fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Use a different account
            </button>
          </div>
        </div>

        {groups.map((g) => (
          <section key={g.name} style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 12, letterSpacing: '.06em', textTransform: 'uppercase',
                         color: '#6b7280', margin: '0 0 8px', fontWeight: 600 }}>
              {g.name}
            </h2>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {g.items.map((s) => {
                const t = SCOPE_TEXT[s];
                return (
                  <li key={s} style={{
                    background: '#fff', border: '1px solid #e5e7eb',
                    borderLeft: `3px solid ${t.heavy ? '#b45309' : '#cbd5e1'}`,
                    borderRadius: 6, padding: '11px 14px', marginBottom: 6,
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {t.label}
                      {t.heavy && (
                        <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700,
                                       color: '#b45309', letterSpacing: '.04em' }}>
                          REACHES CUSTOMERS
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: '#4b5563' }}>{t.detail}</div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}

        {unknown.length > 0 && (
          <section style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 12, letterSpacing: '.06em', textTransform: 'uppercase',
                         color: '#6b7280', margin: '0 0 8px', fontWeight: 600 }}>
              Not recognised
            </h2>
            {unknown.map((s) => (
              <div key={s} style={{ background: '#fff', border: '1px solid #e5e7eb',
                                    borderLeft: '3px solid #b45309', borderRadius: 6,
                                    padding: '11px 14px', marginBottom: 6 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  This application asked for a permission we do not recognise
                </div>
                <div style={{ fontSize: 13, color: '#4b5563', overflowWrap: 'anywhere' }}>{s}</div>
              </div>
            ))}
          </section>
        )}

        {anyHeavy && (
          <p style={{ background: '#fdf4e7', border: '1px solid #f3dcc0', padding: '12px 14px',
                      borderRadius: 6, fontSize: 14, margin: '0 0 16px' }}>
            Some of these reach your customers directly and cost money. Only continue if you
            asked for this.
          </p>
        )}

        {error && (
          <p style={{ background: '#fdeaea', color: '#9b1c1c', padding: '12px 14px',
                      borderRadius: 6, margin: '0 0 16px' }}>
            {error}
          </p>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={approve} disabled={working}
                  style={{ flex: '1 1 160px', padding: 12, border: 0, borderRadius: 6,
                           fontWeight: 600, fontSize: 15, background: '#16a34a', color: '#fff',
                           cursor: working ? 'default' : 'pointer' }}>
            {working ? 'Authorising…' : 'Allow'}
          </button>
          <button onClick={deny} disabled={working}
                  style={{ flex: '1 1 160px', padding: 12, borderRadius: 6, fontWeight: 600,
                           fontSize: 15, background: '#fff', border: '1px solid #cbd3dd',
                           cursor: working ? 'default' : 'pointer' }}>
            Deny
          </button>
        </div>

        <p style={{ fontSize: 12, color: '#6b7280', marginTop: 18 }}>
          You can withdraw this later from the application that asked for it. Access is
          limited to what your own account can already do, and to what {BRAND_CONFIG.brandName}{' '}
          allows for your business.
        </p>
        <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 10, overflowWrap: 'anywhere' }}>
          Application ID {clientId}
        </p>
      </div>
    </div>
  );
};

export default Authorize;
